import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaCommentDots } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import './Styles.css';

import { handleTokenAndPostRequest, trackEvent } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';

const REASONS = [
  { id: 'wrong_property', label: 'It was the wrong property' },
  { id: 'loading_issue', label: 'There was a loading or technical issue' },
  { id: 'took_too_long', label: 'The analysis took too long to load' },
  { id: 'dont_trust_ai', label: "I don't trust AI-generated estimates" },
  { id: 'just_browsing', label: 'I was just browsing / not serious' },
  { id: 'not_useful', label: "The information didn't seem useful to me" },
  { id: 'other', label: 'Other' },
];

function FeedbackIncompleteAnalysis() {
  const { agent_slug, agentDetails, branding } = useAgentDetails();
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [comments, setComments] = useState('');
  const [message, setMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const campaign = params.get('campaign');

  const { accentColor, buttonTextColor, isLight, iconColor, iconBgColor } = branding || {};

  useEffect(() => {
    if (agentDetails) {
      document.title = `${agentDetails.agency_name_short} — Feedback`;
    }
  }, [agentDetails]);

  const toggleReason = (id) => {
    setSelectedReasons((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (selectedReasons.length === 0 && !comments.trim()) {
      setMessage('Please select at least one reason or leave a comment.');
      return;
    }
    trackEvent('submit_incomplete_analysis_feedback', { reasons: selectedReasons });
    setIsUpdating(true);

    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/feedback-incomplete-analysis`,
        {
          agent_slug: agent_slug,
          reasons: selectedReasons,
          comments: comments.trim(),
          campaign: campaign || null,
        },
        { 'Content-Type': 'application/json' }
      );

      setIsUpdating(false);
      setSubmitted(true);
      setMessage('Thank you for your feedback! This helps us improve.');
    } catch (error) {
      console.error('Error:', error);
      setIsUpdating(false);
      setMessage('There was an error submitting your feedback. Please try again.');
    }
  };

  if (!agentDetails) {
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', overflowY: 'auto', backgroundColor: '#f4f5f7' }}>

      <Container style={{ maxWidth: 560, position: 'relative', padding: '40px 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              backgroundColor: `${iconBgColor}14`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FaCommentDots size={22} style={{ color: iconColor }} />
            </div>
            <h1 style={{
              fontSize: '1.6rem', fontWeight: 700, color: '#111',
              margin: 0, letterSpacing: '-0.02em',
            }}>
              Help us improve
            </h1>
          </div>
          <p style={{
            fontSize: '0.92rem', color: '#374151', marginBottom: 0,
            lineHeight: 1.6, maxWidth: 440, margin: '0 auto',
          }}>
            We noticed you started a property analysis but didn't finish. We'd love to know why — your feedback helps us make the experience better.
          </p>
        </div>

        <div style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          padding: '28px 28px',
        }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '2rem', marginBottom: 12 }}>✓</div>
              <p style={{ fontSize: '1rem', fontWeight: 600, color: '#111', marginBottom: 8 }}>Thank you for your feedback!</p>
              <p style={{ fontSize: '0.88rem', color: '#6b7280' }}>This helps us improve the experience for everyone.</p>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: '0.92rem', fontWeight: 600, color: '#111', marginBottom: 14 }}>
                  What prevented you from completing the analysis?
                </p>
                {REASONS.map((reason) => (
                  <div key={reason.id} style={{ marginBottom: 12 }}>
                    <div
                      onClick={() => toggleReason(reason.id)}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}
                    >
                      <div style={{
                        width: 22, height: 22, minWidth: 22, marginTop: 1,
                        borderRadius: 5, border: `2px solid ${selectedReasons.includes(reason.id) ? (accentColor || '#2563eb') : '#d1d5db'}`,
                        backgroundColor: selectedReasons.includes(reason.id) ? (accentColor || '#2563eb') : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}>
                        {selectedReasons.includes(reason.id) && (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M3 7l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: '0.92rem', color: '#1f2937', lineHeight: 1.5 }}>{reason.label}</span>
                    </div>
                    {reason.id === 'wrong_property' && selectedReasons.includes(reason.id) && (
                      <p style={{
                        fontSize: '0.8rem', color: '#6b7280', marginLeft: 34, marginTop: 4, marginBottom: 0,
                        lineHeight: 1.5,
                      }}>
                        💡 You can update the property being tracked from your{' '}
                        <a href="/notifications" target="_blank" rel="noopener noreferrer" style={{ color: accentColor || '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>notification settings</a>.
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <Form.Control
                as="textarea"
                placeholder="Any other comments? (optional)"
                style={{
                  height: 100,
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  fontSize: '0.88rem',
                  padding: '12px 14px',
                  marginBottom: 16,
                  resize: 'none',
                }}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />

              <Button
                type="submit"
                disabled={isUpdating}
                style={{
                  width: '100%',
                  backgroundColor: accentColor,
                  borderColor: isLight ? '#222' : accentColor,
                  color: buttonTextColor,
                  borderRadius: 12,
                  padding: '14px 0',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  boxShadow: `0 4px 16px ${accentColor}30`,
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                {isUpdating ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />
                    Submit Feedback
                  </>
                )}
              </Button>

              {message && (
                <Alert variant="secondary" className="mt-3" style={{ borderRadius: 12, border: 'none', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '0.88rem', textAlign: 'center' }}>
                  {message}
                </Alert>
              )}
            </Form>
          )}
        </div>
      </Container>
    </div>
  );
}

export default FeedbackIncompleteAnalysis;
